import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import Quote from "./Quote";
import "./css/About.css";

const TESTIMONIALS = [
  {
    text: "They've been servicing our garden for a few years now and have transformed our outdoor space into something truly magical. Professional, reliable, and always exceeding expectations.",
    author: "Rich Mason",
    location: "Ada, MI",
  },
  {
    text: "Enchanted Gardener is... enchanting! They take wonderful ideas and turn them into reality. Our backyard is now our favorite room in the house.",
    author: "Suzy Cox",
    location: "Grand Rapids, MI",
  },
  {
    text: "The team's attention to detail is remarkable. They've maintained our property for over five years and it has never looked better.",
    author: "Michael Torres",
    location: "East Grand Rapids, MI",
  },
];

const STATS = [
  { number: "17+", label: "Years of Experience" },
  { number: "500+", label: "Projects Completed" },
  { number: "100%", label: "Client Satisfaction" },
];

export default function About() {
  const navigate = useNavigate();
  const [showQuote, setShowQuote] = useState(false);
  const [activeTestimonial, setActiveTestimonial] = useState(0);
  const [isVisible, setIsVisible] = useState({});
  const heroRef = useRef(null);
  const storyRef = useRef(null);
  const statsRef = useRef(null);
  const testimonialRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsVisible((prev) => ({ ...prev, [entry.target.id]: true }));
          }
        });
      },
      { threshold: 0.1 }
    );

    const refs = [heroRef, storyRef, statsRef, testimonialRef];
    refs.forEach((ref) => {
      if (ref.current) observer.observe(ref.current);
    });

    return () => observer.disconnect();
  }, []);

  // Auto-rotate testimonials
  useEffect(() => {
    const interval = setInterval(() => {
      setActiveTestimonial((prev) => (prev + 1) % TESTIMONIALS.length);
    }, 6000);
    return () => clearInterval(interval);
  }, []);

  const handleServicesClick = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
    navigate("/services");
  };

  return (
    <div className="about-page">
      {/* Hero Section */}
      <section
        id="about-hero"
        ref={heroRef}
        className={`about-hero ${isVisible["about-hero"] ? "visible" : ""}`}
      >
        <div className="about-hero-background">
          <img
            src="/assets/eg-images/flower-chair-eg-og.jpg"
            alt="Garden chair"
          />
          <div className="about-hero-overlay" />
        </div>
        <div className="about-hero-content">
          <span className="section-label animate-fade-in-down">Our Story</span>
          <h1 className="about-hero-title animate-fade-in-up delay-1">
            About Enchanted<br />Gardener
          </h1>
          <p className="about-hero-subtitle animate-fade-in-up delay-2">
            Transforming West Michigan landscapes since 2007
          </p>
        </div>
      </section>

      {/* Story Section */}
      <section
        id="about-story"
        ref={storyRef}
        className={`about-story ${isVisible["about-story"] ? "visible" : ""}`}
      >
        <div className="about-story-container">
          <div className="about-story-images">
            <div className="story-image-main">
              <img
                src="/assets/more-eg-photos/after-backyard.jpg"
                alt="Garden transformation"
              />
            </div>
            <div className="story-image-secondary">
              <img
                src="/assets/more-eg-photos/before-backyard.jpg"
                alt="Before transformation"
              />
            </div>
          </div>

          <div className="about-story-content">
            <h2>Our Mission</h2>
            <p className="lead">
              At Enchanted Gardener, our mission is simple: to remove the burden
              of maintaining your outdoor space, so you can enjoy it to the fullest.
            </p>
            <p>
              Founded in 2007, we've grown from a small local operation into West
              Michigan's trusted garden care specialists. Our passion lies in
              transforming ordinary gardens into extraordinary retreats where
              nature and design harmonize perfectly.
            </p>
            <p>
              Every project we undertake is treated with the same care and attention
              to detail, whether it's weekly maintenance or a complete landscape
              transformation. We believe that beautiful outdoor spaces enhance not
              just properties, but lives.
            </p>

            <div className="story-values">
              <div className="value-item">
                <h4>Quality</h4>
                <p>Excellence in every detail, from plant selection to final placement.</p>
              </div>
              <div className="value-item">
                <h4>Reliability</h4>
                <p>Consistent service you can depend on, season after season.</p>
              </div>
              <div className="value-item">
                <h4>Passion</h4>
                <p>A genuine love for horticulture that shows in our work.</p>
              </div>
            </div>

            <button className="btn-primary-custom" onClick={handleServicesClick}>
              View Our Services
            </button>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section
        id="about-stats"
        ref={statsRef}
        className={`about-stats ${isVisible["about-stats"] ? "visible" : ""}`}
      >
        <div className="stats-container">
          {STATS.map((stat, index) => (
            <div
              key={stat.label}
              className="stat-item"
              style={{ animationDelay: `${index * 100}ms` }}
            >
              <span className="stat-number">{stat.number}</span>
              <span className="stat-label">{stat.label}</span>
            </div>
          ))}
        </div>
      </section>

      {/* Testimonials Section */}
      <section
        id="about-testimonials"
        ref={testimonialRef}
        className={`about-testimonials ${isVisible["about-testimonials"] ? "visible" : ""}`}
      >
        <div className="testimonials-container">
          <span className="section-label">Testimonials</span>
          <h2>What Our Clients Say</h2>

          <div className="testimonials-carousel">
            {TESTIMONIALS.map((testimonial, index) => (
              <blockquote
                key={index}
                className={`testimonial ${index === activeTestimonial ? "active" : ""}`}
              >
                <p>"{testimonial.text}"</p>
                <footer>
                  <span className="testimonial-author">{testimonial.author}</span>
                  <span className="testimonial-location">{testimonial.location}</span>
                </footer>
              </blockquote>
            ))}
          </div>

          <div className="testimonials-indicators">
            {TESTIMONIALS.map((_, index) => (
              <button
                key={index}
                className={`indicator ${index === activeTestimonial ? "active" : ""}`}
                onClick={() => setActiveTestimonial(index)}
                aria-label={`View testimonial ${index + 1}`}
              />
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="about-cta">
        <div className="about-cta-content">
          <h2>Ready to Get Started?</h2>
          <p>
            Let's discuss how we can help transform your outdoor space into
            something extraordinary.
          </p>
          <button
            className="btn-primary-custom"
            onClick={() => setShowQuote(true)}
          >
            Get a Free Quote
          </button>
        </div>
      </section>

      {/* Quote Modal */}
      <Quote show={showQuote} handleClose={() => setShowQuote(false)} />
    </div>
  );
}
